import React, { FC, Fragment, useContext, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    Link,
    MenuItem,
    Paper,
    Select,
    Slider,
    TextField,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Order } from 'models/order';
import { Organization, TransportPartnerService } from 'models/organization';
import TransportPoint from '../shared/transport-point.component';
import { useLazyQuery, useMutation } from '@apollo/client';
import { createNShiftShipmentWaybillsTemplate } from 'apollo/templates/transport';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { LoadingButton } from '@mui/lab';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { TransportProvider, WaybillPackageSize } from 'models/transport';
import * as NshiftServiceConfig from '../../../nshift-service-config.json';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { LabClient } from 'models/client-directory';
import { getLabClientByIdTemplate } from 'apollo/templates/client-directory/query.graphql';
import Loader from 'components/shared/loader/loader.component';

type OrderDeliveryDialogProps = {
    open: boolean;
    onClose: () => void;
    clinic: Organization;
    transportDay: Date;
    orders: Order[];
    noOfManualPackages: number | undefined;
    onShipmentCreate: (clinicId: string, orderIds: string[]) => void;
};
const OrderDeliveryDialog: FC<OrderDeliveryDialogProps> = ({
    open,
    onClose,
    clinic,
    transportDay,
    orders,
    noOfManualPackages,
    onShipmentCreate,
}: OrderDeliveryDialogProps) => {
    const { t: translate } = useTranslation(['orders', 'transport']);
    const { showBoundary } = useDenthubErrorBoundary();
    const [weight, setWeight] = useState<number>(0);
    const [internalMessage, setInternalMessage] = useState<string>();
    const [nShiftServices, setNShiftServices] = useState<TransportPartnerService[]>([]);
    const [selectedService, setSelectedService] = useState('');
    const [transportProvider, setTransportProvider] = useState<TransportProvider>({} as TransportProvider);
    const [partnerId, setPartnerId] = useState('');
    const [selectedPackageSizeId, setSelectedPackageSizeId] = useState<string>();
    const { setAppToasterContent } = useContext(AppToasterContext);
    const lab = currentOrgVar();

    const [getLabClientById, { loading: labClientByIdLoading }] = useLazyQuery(getLabClientByIdTemplate, {
        notifyOnNetworkStatusChange: true,
        onCompleted: ({ labClientById: clientSetting }: { labClientById: LabClient }) => {
            const partner = lab.transportSettings?.nShiftTransport.partners.find((partner) => {
                return partner.id === clientSetting?.transportProvider?.externalId;
            });

            setPartnerId(partner?.id || '');
            setNShiftServices(partner?.services || []);
            setTransportProvider((clientSetting?.transportProvider || {}) as TransportProvider);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        if (open && clinic) {
            getLabClientById({
                variables: {
                    customerId: clinic?._id,
                },
            });
            if (clinic?.name) {
                const serviceId = localStorage.getItem(`${clinic.name}`);
                setSelectedService(serviceId || '');
            }
        }
    }, [open, clinic]);

    const [createNShiftShipmentWaybills, { loading }] = useMutation(createNShiftShipmentWaybillsTemplate, {
        onError: (err: Error) => {
            if (err.message.includes('Phone number is required for clinic')) {
                setAppToasterContent({
                    isOpen: true,
                    severity: 'error',
                    message: translate('transport:ERROR_WAYBILL_CLINIC_PHONE_REQUIRED'),
                });
                return;
            }
            if (err.message.includes('Phone number is required for lab')) {
                setAppToasterContent({
                    isOpen: true,
                    severity: 'error',
                    message: translate('transport:ERROR_WAYBILL_LAB_PHONE_REQUIRED'),
                });
                return;
            }
            showBoundary(err);
        },
        onCompleted: () => {
            onShipmentCreate(
                clinic._id,
                orders.map((order) => order.orderId),
            );
            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: translate('transport:CREATE_PACKAGE_SUCCESS'),
            });
        },
    });

    const createWaybills = () => {
        createNShiftShipmentWaybills({
            variables: {
                clinic: clinic._id,
                transportDay: transportDay,
                manualPackageCount: noOfManualPackages,
                weight: weight,
                internalMessage: internalMessage,
                partnerId: partnerId,
                service: selectedService,
                orderIds: orders.map((order) => order.orderId),
                packageSizeId: selectedPackageSizeId,
            },
        });
    };

    const handleWeightChange = (event: Event, value: number | number[]) => {
        setWeight(value as number);
    };

    const handleInternalMessageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setInternalMessage(event.target.value);
    };

    const handleServiceChange = (serviceId: string) => {
        localStorage.setItem(`${clinic.name}`, serviceId);
        setSelectedService(serviceId);
    };

    const valueText = (value: number) => {
        return `${value} ${NshiftServiceConfig[selectedService as keyof typeof NshiftServiceConfig].unit}`;
    };

    const config = () => {
        const config = NshiftServiceConfig[selectedService as keyof typeof NshiftServiceConfig];
        if (!config) {
            throw new Error('NShift Service config missing');
        }
        return config;
    };

    const handlePackageSizeChange = (sizeId: string) => {
        setSelectedPackageSizeId(sizeId);
    };

    const isFormValid = () => {
        return !weight || !selectedService || (config().packageSizeRequired && !selectedPackageSizeId);
    };

    if (labClientByIdLoading) {
        return <Loader />;
    }

    return (
        <Fragment>
            <Dialog maxWidth="md" fullWidth open={open} onClose={onClose}>
                <DialogTitle>{translate('transport:CREATE_PACKAGE')}</DialogTitle>
                <DialogContent>
                    <Box mt={1}>
                        <Box mb={2}>
                            <Paper variant="outlined">
                                <Box p={1}>
                                    <TransportPoint
                                        location={{
                                            organizationId: clinic._id,
                                            name: clinic.name,
                                            streetAddress: clinic.contact.address.street || '',
                                            zipCode: clinic.contact.address.postalCode || '',
                                            city: clinic.contact.address.city || '',
                                        }}
                                        isFrom={false}
                                        date={transportDay.toDateString()}
                                    />
                                </Box>
                            </Paper>
                        </Box>

                        <Box mb={2}>
                            <FormControl fullWidth>
                                <InputLabel id="transport-provider-label">
                                    {translate('transport:TRANSPORT_SERVICE')}
                                </InputLabel>
                                <Select
                                    labelId="transport-provider-label"
                                    value={selectedService}
                                    label={translate('transport:TRANSPORT_SERVICE')}
                                    onChange={(e) => handleServiceChange(e.target.value)}
                                >
                                    {nShiftServices?.map((service: TransportPartnerService, index: number) => {
                                        return (
                                            <MenuItem key={index} value={service.id}>
                                                {service.description}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box mb={2}>
                            <Grid container>
                                <Grid item xs={12} md={10}>
                                    <Box>
                                        <Typography variant="subtitle2">{translate('orders:ORDERS')}</Typography>
                                        {orders.map((order) => (
                                            <Link
                                                key={order._id}
                                                target="_blank"
                                                href={`/order-details/${order._id}/main-view`}
                                                sx={{ marginRight: 1 }}
                                            >
                                                {order.orderId}
                                            </Link>
                                        ))}
                                    </Box>
                                    {noOfManualPackages ? (
                                        <Box>
                                            {noOfManualPackages} {translate('transport:PACKAGE_WITHOUT_ORDER')}
                                        </Box>
                                    ) : undefined}
                                </Grid>
                                <Grid item sx={{ textAlign: 'right' }} xs={12} md={2}>
                                    <img
                                        style={{ maxHeight: 20 }}
                                        src={`/images/transport-logos/${transportProvider.externalId}.png`}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                        {selectedService ? (
                            <Box mt={4} mb={2}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Box mx={2}>
                                            <Slider
                                                aria-label="Small steps"
                                                defaultValue={0}
                                                valueLabelFormat={valueText}
                                                step={config().step}
                                                marks={config().marks}
                                                min={config().minWeight}
                                                max={config().maxWeight}
                                                onChange={handleWeightChange}
                                                valueLabelDisplay="on"
                                            />
                                        </Box>
                                    </Grid>

                                    {config().packageSizeRequired ? (
                                        <Grid item xs={12}>
                                            <FormControl fullWidth required>
                                                <InputLabel id="transport-provider-label">
                                                    {translate('transport:WAYBILL_PACKAGE_SIZE')}
                                                </InputLabel>
                                                <Select
                                                    labelId="transport-provider-label"
                                                    value={selectedPackageSizeId}
                                                    label={translate('transport:WAYBILL_PACKAGE_SIZE')}
                                                    onChange={(e) => handlePackageSizeChange(e.target.value)}
                                                >
                                                    {lab.transportSettings?.nShiftTransport?.waybillPackageSizes?.map(
                                                        (size: WaybillPackageSize) => {
                                                            return (
                                                                <MenuItem key={size._id} value={size._id}>
                                                                    <Box mr={1}>
                                                                        <Typography>{size.name}</Typography>
                                                                    </Box>
                                                                    <Box>
                                                                        <Typography
                                                                            variant="caption"
                                                                            color={'text.secondary'}
                                                                        >
                                                                            ({size.width}cm x {size.height}cm x{' '}
                                                                            {size.length}cm)
                                                                        </Typography>
                                                                    </Box>
                                                                </MenuItem>
                                                            );
                                                        },
                                                    )}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    ) : undefined}

                                    <Grid item xs={12}>
                                        <TextField
                                            onChange={handleInternalMessageChange}
                                            label={translate('transport:INTERNAL_MESSAGE')}
                                            fullWidth
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        ) : undefined}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>{translate('common:CANCEL')}</Button>
                    <LoadingButton
                        disabled={isFormValid()}
                        loading={loading}
                        variant="contained"
                        onClick={createWaybills}
                    >
                        {translate('transport:CREATE_PACKAGE')}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Fragment>
    );
};

export default OrderDeliveryDialog;
